import ChunkedUploady from '@rpldy/chunked-uploady';
import React, { createContext, createRef, useCallback, useContext } from 'react';
import { addFilesToUploadQueueAction, clearFinishedAction, setDirectoryInputAction, setFileStatusAction, useUploaderReducer } from '../../../services/file-explorer/uploader/uploaderReducer';
import urls from '../../../services/urls';
import UploaderApiCallModifier from './UploaderApiCallModifier';
import { useUploady, useBatchAddListener, useItemStartListener, useItemProgressListener, useItemFinishListener, useItemErrorListener } from "@rpldy/uploady";
import retryEnhancer from "@rpldy/retry-hooks";
import { useRetry } from "@rpldy/retry-hooks";
import { concatenateFileSystemNodePathToFilePath, findQueueItem } from '../../../services/file-explorer/uploader/uploaderUtils';
import { useQueryClient } from 'react-query';
import UploaderAddDialogProvider from './uploader-add-dialog/UploaderAddDialogProvider';

export function Uploader({children, fileSystemNodePath}) {

    return (
        <ChunkedUploady
            destination={{url: urls.apiBase + '/resource', method: 'POST'}}
            multiple
            sendWithFormData={true}
            inputFieldName={"file"}
            chunkSize={2000000}
            enhancer={retryEnhancer}
        >
            <UploadyWrappedUploader fileSystemNodePath={fileSystemNodePath}>
                <UploaderAddDialogProvider fileSystemNodePath={fileSystemNodePath}>
                    <UploaderApiCallModifier/>
                    {children}
                </UploaderAddDialogProvider>
            </UploadyWrappedUploader>
        </ChunkedUploady>
    );
}

const UploaderContext = createContext();
export const useUploader = () => useContext(UploaderContext);

function UploadyWrappedUploader(props) {
    const { children, fileSystemNodePath } = props;
    const [state, dispatch] = useUploaderReducer();
    const slashConcatenatedFileSystemNodePath = fileSystemNodePath !== null ? fileSystemNodePath.join('/') : '';
    const uploady = useUploady();
    const queryClient = useQueryClient();
    const retry = useRetry();
    const uploadFileInputRef = createRef();
    const uploadDirectoryInputRef = createRef();

    const addFilesToUploadQueue = (files, tagIds) => {
        for(var file of files) {
            file.tagIds = tagIds;
        }
        uploady.upload(files);
    }

    const retryToUploadFailedFiles = useCallback(() => {
        retry();
    }, [retry]);

    const clearFinishedFilesFromUploadQueue = () => {
        dispatch(new clearFinishedAction());
    }

    const openFileUploadDialog = () => {
        dispatch(new setDirectoryInputAction(false));
        uploadFileInputRef.current.click();
    }

    const openDirectoryUploadDialog = () => {
        dispatch(new setDirectoryInputAction(true));
        uploadDirectoryInputRef.current.click();
    }

    useBatchAddListener((batch) => {
        dispatch(new addFilesToUploadQueueAction(batch.items.map((item) => {
            return {
                id: item.id,
                path: concatenateFileSystemNodePathToFilePath(slashConcatenatedFileSystemNodePath, item.file.path),
                rootPath: slashConcatenatedFileSystemNodePath,
                tagIds: item.file.tagIds,
                status: 'added',
                progress: 0,
            };
        })))
    });

    useItemStartListener((item) => {
        dispatch(new setFileStatusAction(item.id, 'processing', item.completed));
    });

    useItemProgressListener((item) => {
        dispatch(new setFileStatusAction(item.id, 'processing', item.completed));
    });

    useItemFinishListener((item) => {
        dispatch(new setFileStatusAction(item.id, 'finished', item.completed));

        let queueItem = findQueueItem(state.queue, item.id);
        let path = queueItem.path.split("/");

        let subpath = [];
        for (let partialPath of path) {
            queryClient.invalidateQueries(['/file-explorer/search', 'getDirectoryTree', subpath.join("/")]);
            subpath.push(partialPath);
        }
        queryClient.invalidateQueries(['/file-explorer/search', 'getDirectoryContent']);
    });

    useItemErrorListener((item) => {
        dispatch(new setFileStatusAction(item.id, 'failed', item.completed));
    });

    return (
        <UploaderContext.Provider value={{ state, uploadFileInputRef, uploadDirectoryInputRef, openFileUploadDialog, openDirectoryUploadDialog, addFilesToUploadQueue, retryToUploadFailedFiles, clearFinishedFilesFromUploadQueue }}>
            {children}
        </UploaderContext.Provider>
    );
}
